import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ListRenderItemInfo } from 'react-native';
import { useIsFocused } from 'react-navigation-hooks';

import { useOrientation } from '../../../boot/OrientationContext';
import { checkItemViewed } from '../../../context/devLogFirehose';
import BannerCarousel, {
  ExportProperties,
} from '../../../elements/BannerCarousel';
import useActions from '../../../hooks/useActions';
import getBannersActions from '../../../redux/actions/getBannersActions';
import {
  makeBannerIDs,
  makeIsBannersLoading,
} from '../../../redux/selectors/bannersSelector';
import { useSelector } from '../../../redux/utils';
import useHomeReloadEffect from '../hooks/useHomeReloadEffect';
import PromoteBanner, {
  layoutName as bannerLayoutName,
  bannerPositions,
} from '../PromoteBanner';
import PromoteItem, {
  layoutName as promoteItemLayoutName,
} from '../PromoteItem';
import S, { getBannerSize, styles } from './styles';
import usePromoteItemImpression from './usePromoteItemImpression';

export interface Methods {
  setIsVisible: (isVisible: boolean) => void;
}

interface Props {
  promoteItems: string[];
}

const paginationDotStyle = { longWidth: 16, shortWidth: 8 };

const PromoteSection = forwardRef<Methods, Props>((props, ref) => {
  const isLandscape = useOrientation();

  const { promoteItems } = props;

  const bannerIDs = useSelector(makeBannerIDs(bannerLayoutName));

  const [isVisible, setIsVisible] = useState(false);
  const isFocused = useIsFocused();
  const isCarouselEnabled = isVisible && isFocused;

  const carouselRef = useRef<ExportProperties>(null);

  useImperativeHandle(ref, () => ({
    setIsVisible,
  }));

  const isLoading = useSelector(makeIsBannersLoading(bannerLayoutName));
  const getBanners = useActions(getBannersActions.request);
  const bannerSize = useMemo(() => getBannerSize(), [isLandscape]);

  const [accItems, clearImpressions] = usePromoteItemImpression({
    enabled: isCarouselEnabled,
    promoteItems,
    layoutName: promoteItemLayoutName,
  });

  const renderPromoteItems = useCallback(
    (item, index) => {
      const height = bannerSize.height / 2;

      return (
        <PromoteItem
          id={item}
          key={item}
          height={height}
          index={index}
          showLoading={/blank/.test(`${item}`)}
          layoutName={promoteItemLayoutName}
          shouldDisplayImpressionForDebug={checkItemViewed(
            accItems.current,
            item
          )}
        />
      );
    },
    [bannerSize.height]
  );

  useHomeReloadEffect(() => {
    if (!isLoading) {
      clearImpressions();
      getBanners({
        positions: bannerPositions,
        layoutName: bannerLayoutName,
        loadingType: 'reload',
      });
    }
  }, [getBanners, isLoading, clearImpressions]);

  const renderSlide = useCallback(
    ({ item: bannerID, index }: ListRenderItemInfo<string>) => {
      return (
        <PromoteBanner
          key={bannerID}
          bannerID={bannerID}
          index={index}
          showLoading={/^blank/.test(bannerID)}
          bannerSize={bannerSize}
          layoutName={bannerLayoutName}
          shouldDisplayImpressionForDebug={checkItemViewed(
            carouselRef.current?.accumulatedItems,
            bannerID
          )}
        />
      );
    },
    [bannerSize]
  );

  return (
    <S.Section>
      <S.BannerBlock width={bannerSize.width}>
        <BannerCarousel
          ref={carouselRef}
          bannerWidth={bannerSize.width}
          carouselData={bannerIDs}
          renderSlide={renderSlide}
          play={isCarouselEnabled}
          paginationWrapperStyle={styles.pagination}
          dotStyle={paginationDotStyle}
          layoutName={bannerLayoutName}
          isVisible={isCarouselEnabled}
          shouldLogImpression
        />
      </S.BannerBlock>
      <S.FlexBlock>{promoteItems.map(renderPromoteItems)}</S.FlexBlock>
    </S.Section>
  );
});

export default PromoteSection;
